import { APP_INITIALIZER, NgModule } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { IonicModule, IonicRouteStrategy, NavController } from '@ionic/angular';
import { CallNumber } from '@awesome-cordova-plugins/call-number/ngx';
import { Network } from '@awesome-cordova-plugins/network/ngx';
import { MomentModule } from 'ngx-moment';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { CoreDataService } from '@app/core/async-services/http/core.data';
import { SharedModule } from '@app/shared';
import { StorageModule } from '@app/core/storage';
import { UpdateVersionGuard, UserAccessGuard } from '@app/core/guards';
import {
  PermissionParserService,
  AlertService,
  OverlayService,
  RouterParamsService,
  RouteSnapshotHistoryService,
  NetworkConnectionService,
  RouterPreloaderService,
  PhoneService,
  AuthService,
  ClearStateService,
} from '@app/core/services';
import { MenuOptionsService } from '@app/core/services/menu-options';
import { HotelGroupsService } from '@app/core/services/hotel-groups';
import { AuthUtilityService, CommonUtils } from '@app/core/utils';
import { HttpInterceptorProviders } from '@app/core/async-services/interceptors';
import { File } from '@awesome-cordova-plugins/file/ngx';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { reducers, metaReducers, effects } from './store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '@env/environment';
import { LoggerModule } from 'ngx-logger';
import { FileOpener } from '@awesome-cordova-plugins/file-opener/ngx';
import { FileTransfer } from '@awesome-cordova-plugins/file-transfer/ngx';
import { DocumentPicker } from '@awesome-cordova-plugins/document-picker/ngx';
import { FileChooser } from '@awesome-cordova-plugins/file-chooser/ngx';
import { FilePath } from '@awesome-cordova-plugins/file-path/ngx';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import * as Icons from './icons';
import {
  StoreRouterConnectingModule,
  MinimalRouterStateSerializer,
} from '@ngrx/router-store';
import { ServiceWorkerModule } from '@angular/service-worker';
import { first, iif, Observable, of, switchMap, tap } from 'rxjs';
import { VersionConfigService } from './core/services/version-config';
import { AuthHttpService } from './core/async-services/http';

function initializeAppFactory(
  authService: AuthService,
  authHttpService: AuthHttpService,
  clearStateService: ClearStateService,
  navController: NavController,
  versionConfigService: VersionConfigService,
): () => Observable<any> {
  return () =>
    versionConfigService.fetchVersionConfig().pipe(
      first(),
      switchMap(() => authService.isAuthenticated()),
      switchMap((isAuthenticated) => {
        return iif(
          () => !isAuthenticated,
          of(true),
          authHttpService.getSession().pipe(
            tap(async (isSessionValid) => {
              if (!isSessionValid) {
                localStorage.clear();
                clearStateService.clearState();
                await authService.clearAllSessionCookies();
                navController.navigateRoot('signout', {
                  queryParams: {
                    /* eslint-disable @typescript-eslint/naming-convention */
                    title_id: 'AUTH_SESSION_EXPIRED_TITLE',
                    message_id: 'AUTH_SESSION_EXPIRED_MESSAGE',
                  },
                });
              }
            }),
            switchMap((isSessionValid) =>
              iif(
                () => isSessionValid,
                authService.initializeCoreData(),
                of(true),
              ),
            ),
          ),
        );
      }),
    );
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    MomentModule,
    SharedModule,
    BrowserModule,
    CommonModule,
    IonicModule.forRoot({
      mode: 'ios',
    }),
    ServiceWorkerModule.register('./ngsw-worker.js', {
      enabled: environment.serviceWorker,
    }),
    AppRoutingModule,
    StorageModule,
    LoggerModule.forRoot(environment.logConfig),
    EffectsModule.forRoot(effects),
    StoreModule.forRoot(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
        strictStateSerializability: true,
        strictActionSerializability: true,
        strictActionWithinNgZone: true,
        strictActionTypeUniqueness: true,
      },
    }),
    StoreDevtoolsModule.instrument({
      logOnly: environment.production,
      connectInZone: true,
    }),
    StoreRouterConnectingModule.forRoot({
      serializer: MinimalRouterStateSerializer,
    }),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      deps: [
        AuthService,
        AuthHttpService,
        ClearStateService,
        NavController,
        VersionConfigService,
      ],
      multi: true,
    },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    UpdateVersionGuard,
    UserAccessGuard,
    CoreDataService,
    AuthUtilityService,
    CommonUtils,
    PermissionParserService,
    AlertService,
    OverlayService,
    MenuOptionsService,
    HotelGroupsService,
    DatePipe,
    CallNumber,
    RouterParamsService,
    RouteSnapshotHistoryService,
    HttpInterceptorProviders,
    RouterPreloaderService,
    PhoneService,
    NetworkConnectionService,
    Network,
    File,
    FileOpener,
    FileTransfer,
    DocumentPicker,
    FileChooser,
    FilePath,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  constructor(library: FaIconLibrary) {
    library.addIcons(...Object.values(Icons));
  }
}
